/**
 * แสดงประกาศแบบ Sweet Alert พร้อมปุ่ม «รับทราบ» สำหรับ user-modern
 * โหลดจาก tenant-management/announcements/active (target=user-modern)
 * รับ event announcement_refetch เพื่อ refetch ทันทีเมื่อแอดมินกด "ส่งทันที"
 *
 * ปรับปรุง:
 * - หยุด poll เมื่อ tab ไม่ active (Page Visibility API)
 * - interval 120s (เดิม 60s)
 * - ใช้ useRef เก็บ username ป้องกัน loadActive ถูก recreate ทุก render
 */
import React, { useState, useEffect, useCallback, useRef } from 'react'
import Swal from 'sweetalert2'
import { announcementService, type Announcement } from '../services/announcementService'
import * as authService from '../services/authService'

const STORAGE_KEY = 'huay_modern_announcement_acked'
const POLL_INTERVAL_MS = 120 * 1000

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

function formatContentHtml(value: string): string {
  return escapeHtml(value).replace(/\n/g, '<br />')
}

/** คืนค่า { [announcementId]: ackedAtTimestamp } รองรับรูปแบบเก่า (array of id) */
function getAckedMap(): Record<string, number> {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return {}
    const parsed = JSON.parse(raw)
    if (Array.isArray(parsed)) {
      return parsed.reduce((acc: Record<string, number>, id: string) => ({ ...acc, [id]: 0 }), {})
    }
    return typeof parsed === 'object' && parsed !== null ? parsed : {}
  } catch {
    return {}
  }
}

function addAckedId(id: string) {
  const map = getAckedMap()
  map[id] = Date.now()
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(map))
}

/** ลบ id ออกจากรายการรับทราบแล้ว — ใช้เมื่อแอดมินกด "ส่งทันที" เพื่อให้ประกาศโผล่อีกครั้ง */
function removeAckedId(id: string) {
  const map = getAckedMap()
  delete map[id]
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(map))
}

/** ถ้าประกาศเปิดส่งซ้ำ และครบ interval แล้ว แสดงอีกครั้ง */
function shouldShowAnnouncement(a: Announcement, ackedMap: Record<string, number>): boolean {
  const ackedAt = ackedMap[a._id]
  if (ackedAt === undefined) return true
  if (a.repeat?.enabled && a.repeat?.intervalMinutes) {
    const intervalMs = a.repeat.intervalMinutes * 60 * 1000
    if (Date.now() - ackedAt >= intervalMs) return true
  }
  return false
}

interface ModernAnnouncementAlertProps {
  tenantId: string
}

export const ModernAnnouncementAlert: React.FC<ModernAnnouncementAlertProps> = ({ tenantId }) => {
  const [items, setItems] = useState<Announcement[]>([])
  const [currentIndex, setCurrentIndex] = useState(0)
  const [loading, setLoading] = useState(true)
  const [refetchTrigger, setRefetchTrigger] = useState(0)
  const lastShownIdRef = useRef<string | null>(null)

  // useRef เก็บ username เพื่อไม่ให้ loadActive ถูก recreate ทุก render ที่ username เปลี่ยน
  const usernameRef = useRef<string | null>(authService.getCurrentUser()?.username ?? null)

  const loadActive = useCallback(async (options?: { bypassCache?: boolean }) => {
    if (!tenantId) return
    // หยุดเมื่อ tab ไม่ active — ไม่ต้องเรียก API เมื่อผู้ใช้ไม่ได้ดูหน้าจอ
    if (document.visibilityState === 'hidden') return
    setLoading(true)
    try {
      const res = await announcementService.getActive(tenantId, usernameRef.current, options)
      if (res.success && res.data?.length) {
        const ackedMap = getAckedMap()
        const notAcked = res.data.filter((a) => shouldShowAnnouncement(a, ackedMap))
        setItems(notAcked)
        setCurrentIndex(0)
        if (import.meta.env.DEV && notAcked.length > 0) {
          console.log('[Announcements] Loaded', notAcked.length, 'to show', notAcked[0]?.title)
        }
      } else {
        setItems([])
      }
    } catch (e) {
      if (import.meta.env.DEV) console.warn('[Announcements] loadActive error', e)
      setItems([])
    } finally {
      setLoading(false)
    }
  }, [tenantId]) // ลบ username ออกจาก dep — ใช้ ref แทน ป้องกัน recreate ทุก render

  useEffect(() => {
    loadActive()
    const t = setInterval(loadActive, POLL_INTERVAL_MS)

    // เมื่อ tab กลับมา visible ให้ load ทันทีแทนรอ interval ถัดไป
    const handleVisible = () => {
      if (document.visibilityState === 'visible') loadActive()
    }
    document.addEventListener('visibilitychange', handleVisible)

    return () => {
      clearInterval(t)
      document.removeEventListener('visibilitychange', handleVisible)
    }
  }, [loadActive])

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ announcementId?: string }>)?.detail
      const id = detail?.announcementId
      if (id) {
        removeAckedId(id)
        if (import.meta.env.DEV) console.log('[Announcements] send-now: cleared acked for', id)
      }
      announcementService.invalidateCache(tenantId, usernameRef.current)
      if (import.meta.env.DEV) console.log('[Announcements] refetch event received')
      lastShownIdRef.current = null
      setRefetchTrigger((t) => t + 1)
      loadActive({ bypassCache: true })
    }
    window.addEventListener('announcement_refetch', handler)
    return () => window.removeEventListener('announcement_refetch', handler)
  }, [loadActive])

  const current = items[currentIndex]
  const hasNext = currentIndex < items.length - 1

  const showCurrent = useCallback(() => {
    if (!current) return
    const isWarning = current.priority === 'warning'
    const isUrgent = current.priority === 'urgent'
    const thisId = current._id
    const accentColor = isUrgent ? '#dc2626' : isWarning ? '#d97706' : '#2563eb'
    const accentBadgeBg = isUrgent ? 'rgba(220,38,38,0.12)' : isWarning ? 'rgba(217,119,6,0.12)' : 'rgba(37,99,235,0.12)'
    const closeButtonTone = current.imageUrl
      ? 'border:1px solid rgba(255,255,255,0.82);background:rgba(15,23,42,0.58);color:#fff;'
      : 'border:1px solid rgba(15,23,42,0.12);background:rgba(255,255,255,0.94);color:#0f172a;'
    const imageHtml = current.imageUrl
      ? `
          <div style="position:relative;background:#0f172a;">
            <img
              src="${escapeHtml(current.imageUrl)}"
              alt="${escapeHtml(current.title)}"
              style="display:block;width:100%;max-height:340px;object-fit:cover;"
              onerror="this.parentElement.style.display='none'"
            />
            <button
              id="announcement-close-button"
              type="button"
              aria-label="Close announcement"
              style="position:absolute;top:14px;right:14px;display:flex;align-items:center;justify-content:center;width:40px;height:40px;border-radius:999px;cursor:pointer;font-size:24px;font-weight:400;line-height:1;backdrop-filter:blur(10px);${closeButtonTone}"
            >
              ×
            </button>
          </div>
        `
      : `
          <button
            id="announcement-close-button"
            type="button"
            aria-label="Close announcement"
            style="position:absolute;top:16px;right:16px;display:flex;align-items:center;justify-content:center;width:40px;height:40px;border-radius:999px;cursor:pointer;font-size:24px;font-weight:400;line-height:1;backdrop-filter:blur(10px);${closeButtonTone}"
          >
            ×
          </button>
        `
    const priorityLabel = isUrgent ? 'ประกาศด่วน' : isWarning ? 'ประกาศสำคัญ' : 'ประกาศ'
    Swal.fire({
      title: '',
      html: `
        <div style="position:relative;overflow:hidden;border-radius:26px;background:#fff;text-align:left;box-shadow:0 28px 80px rgba(15,23,42,0.35);">
          ${imageHtml}
          <div style="padding:${current.imageUrl ? '22px 24px 24px' : '24px'};">
            <div style="display:inline-flex;align-items:center;gap:8px;padding:6px 12px;border-radius:999px;background:${accentBadgeBg};color:${accentColor};font-size:12px;font-weight:700;letter-spacing:0.02em;">
              ${priorityLabel}
            </div>
            <div style="margin-top:14px;color:#0f172a;font-size:31px;line-height:1.18;font-weight:800;letter-spacing:-0.02em;word-break:break-word;">
              ${escapeHtml(current.title)}
            </div>
            <div style="margin-top:12px;color:#475569;font-size:15px;line-height:1.85;word-break:break-word;">
              ${formatContentHtml(current.content)}
            </div>
            <div style="margin-top:22px;display:flex;justify-content:center;">
              <button
                id="announcement-acknowledge-button"
                type="button"
                style="min-width:148px;border:none;border-radius:14px;padding:13px 24px;background:${accentColor};color:#fff;font-size:16px;font-weight:700;cursor:pointer;box-shadow:0 16px 32px ${accentBadgeBg};"
              >
                รับทราบ
              </button>
            </div>
          </div>
        </div>
      `,
      showConfirmButton: false,
      padding: 0,
      background: 'transparent',
      backdrop: 'rgba(2, 6, 23, 0.72)',
      width: 'min(92vw, 560px)',
      customClass: {
        popup: 'announcement-popup-card',
        htmlContainer: 'announcement-popup-html',
      },
      allowOutsideClick: !!current.requireAck,
      allowEscapeKey: !!current.requireAck,
      didOpen: () => {
        const popup = Swal.getPopup()
        if (popup) {
          popup.style.padding = '0'
          popup.style.background = 'transparent'
          popup.style.boxShadow = 'none'
        }

        const closeButton = document.getElementById('announcement-close-button')
        const acknowledgeButton = document.getElementById('announcement-acknowledge-button')
        closeButton?.addEventListener('click', () => Swal.close())
        acknowledgeButton?.addEventListener('click', () => Swal.close())
      },
    }).then(() => {
      announcementService.acknowledge(thisId, tenantId).catch(() => {})
      addAckedId(thisId)
      // ล้าง ref เพื่อให้ส่งซ้ำทุก X นาที แสดงประกาศเดิมอีกครั้งได้
      lastShownIdRef.current = null
      if (hasNext) {
        setCurrentIndex((prev) => prev + 1)
      } else {
        setItems((prev) => prev.filter((x) => x._id !== thisId))
        setCurrentIndex(0)
      }
    })
  }, [current, tenantId, hasNext])

  useEffect(() => {
    if (!loading && current && items.length > 0 && lastShownIdRef.current !== current._id) {
      lastShownIdRef.current = current._id
      const t = setTimeout(() => showCurrent(), 0)
      return () => clearTimeout(t)
    }
  }, [loading, current?._id, items.length, showCurrent, refetchTrigger])

  return null
}
